/**
 * TeamDetailModal - Detailed team view with tabs
 *
 * Tabs:
 * 1. Vue d'ensemble - team info, leader, site
 * 2. Membres - accordions for drivers/vehicles/tools with add/remove
 * 3. KPIs - cost + productivity gauges
 * 4. Historique - membership timeline
 */
import React, { useState } from 'react';
import { useFormat } from '../../i18n/format.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Users, BarChart3, Clock, Info, User, Car, Wrench, Plus, Trash2,
  MapPin, RefreshCw, ChevronDown, ChevronRight, TrendingUp, DollarSign
} from 'lucide-react';
import { teamAPI } from '../../api';
import MemberPickerModal from './MemberPickerModal.jsx';
import TeamFormModal from './TeamFormModal.jsx';

// Les libelles d'onglet viennent de i18n (teams.detail.tabs.*).
const TABS = [
  { id: 'overview', Icon: Info },
  { id: 'members', Icon: Users },
  { id: 'kpis', Icon: BarChart3 },
  { id: 'history', Icon: Clock },
];

export default function TeamDetailModal({ teamId, onClose, onUpdated }) {
  const { t, fmt } = useFormat();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showMemberPicker, setShowMemberPicker] = useState(null); // 'driver' | 'vehicle' | 'tool' | null
  const [showEditForm, setShowEditForm] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState('month');
  const [expandedSections, setExpandedSections] = useState({ drivers: true, vehicles: true, tools: true });

  const { data: teamData, isLoading } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamAPI.getById(teamId),
    enabled: !!teamId,
  });

  const { data: kpiData, isLoading: kpiLoading } = useQuery({
    queryKey: ['team-kpis', teamId, kpiPeriod],
    queryFn: () => teamAPI.getKPIs(teamId, kpiPeriod),
    enabled: activeTab === 'kpis' && !!teamId,
  });

  const { data: historyData } = useQuery({
    queryKey: ['team-history', teamId],
    queryFn: () => teamAPI.getMemberHistory(teamId),
    enabled: activeTab === 'history' && !!teamId,
  });

  const team = teamData?.data;
  const kpis = kpiData?.data;
  const history = historyData?.data || [];

  const handleAddMember = async (memberType, memberId) => {
    try {
      await teamAPI.addMember(teamId, { member_type: memberType, member_id: memberId });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      onUpdated?.();
      setShowMemberPicker(null);
    } catch (err) {
      alert(err.message || t('teams.errors.addMember'));
    }
  };

  const handleRemoveMember = async (memberId, memberType, memberEntityId) => {
    if (!window.confirm(t('teams.detail.confirmRemove'))) return;
    try {
      await teamAPI.removeMember(teamId, memberId);
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      onUpdated?.();
    } catch (err) {
      alert(err.message || t('teams.errors.removeMember'));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaved = () => {
    setShowEditForm(false);
    queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    onUpdated?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            {team?.color && (
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: team.color }} />
            )}
            <div>
              <h2 className="text-lg font-bold text-gray-900">{team?.name || t('common.states.loading')}</h2>
              <p className="text-xs text-gray-500">{team?.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {team && (
              <button
                onClick={() => setShowEditForm(true)}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {t('common.actions.edit')}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-4">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.Icon className="h-4 w-4" />
              {t(`teams.detail.tabs.${tab.id}`)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && team && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField label={t('teams.detail.fields.type')} value={t(`teams.type.${team.team_type}`, { defaultValue: team.team_type })} />
                    <InfoField label={t('teams.detail.fields.status')} value={t(`teams.status.${team.status}`, { defaultValue: team.status })} />
                    <InfoField label={t('teams.detail.fields.maxMembers')} value={team.max_members} />
                    <InfoField
                      label={t('teams.detail.fields.leader')}
                      value={team.team_lead ? `${team.team_lead.first_name} ${team.team_lead.last_name}` : t('teams.detail.fields.leaderUnset')}
                    />
                  </div>
                  {team.site && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{team.site.name}</span>
                      {team.site.address && <span className="text-gray-400">- {team.site.address}</span>}
                    </div>
                  )}
                  {team.description && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-600">{team.description}</p>
                    </div>
                  )}
                  {team.notes && (
                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-100">
                      <p className="text-sm text-yellow-800">{team.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Members Tab */}
              {activeTab === 'members' && team && (
                <div className="space-y-3">
                  {/* Drivers */}
                  <MemberSection
                    title={t('teams.detail.sections.drivers')}
                    icon={User}
                    members={team.members?.drivers || []}
                    expanded={expandedSections.drivers}
                    onToggle={() => toggleSection('drivers')}
                    onAdd={() => setShowMemberPicker('driver')}
                    onRemove={handleRemoveMember}
                    removeLabel={t('teams.detail.removeMemberTitle')}
                    addLabel={t('common.actions.add')}
                    renderItem={(m) => `${m.entity?.first_name || '?'} ${m.entity?.last_name || '?'}`}
                    renderSub={(m) => m.entity?.email || ''}
                  />
                  {/* Vehicles */}
                  <MemberSection
                    title={t('teams.detail.sections.vehicles')}
                    icon={Car}
                    members={team.members?.vehicles || []}
                    expanded={expandedSections.vehicles}
                    onToggle={() => toggleSection('vehicles')}
                    onAdd={() => setShowMemberPicker('vehicle')}
                    onRemove={handleRemoveMember}
                    removeLabel={t('teams.detail.removeMemberTitle')}
                    addLabel={t('common.actions.add')}
                    renderItem={(m) => `${m.entity?.make || ''} ${m.entity?.model || ''} (${m.entity?.license_plate || '?'})`}
                    renderSub={(m) => m.entity?.vin || ''}
                  />
                  {/* Tools */}
                  <MemberSection
                    title={t('teams.detail.sections.tools')}
                    icon={Wrench}
                    members={team.members?.tools || []}
                    expanded={expandedSections.tools}
                    onToggle={() => toggleSection('tools')}
                    onAdd={() => setShowMemberPicker('tool')}
                    onRemove={handleRemoveMember}
                    removeLabel={t('teams.detail.removeMemberTitle')}
                    addLabel={t('common.actions.add')}
                    renderItem={(m) => m.entity?.name || t('teams.detail.toolFallback', { id: m.member_id })}
                    renderSub={(m) => m.entity?.tool_code || ''}
                  />
                </div>
              )}

              {/* KPIs Tab */}
              {activeTab === 'kpis' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    {['month', 'quarter', 'year'].map(p => (
                      <button
                        key={p}
                        onClick={() => setKpiPeriod(p)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          kpiPeriod === p ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {t(`teams.detail.period.${p}`)}
                      </button>
                    ))}
                  </div>

                  {kpiLoading ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="h-5 w-5 animate-spin text-indigo-500" />
                    </div>
                  ) : kpis ? (
                    <div className="grid grid-cols-2 gap-3">
                      <KPICard icon={DollarSign} label={t('teams.detail.kpi.fuelCost')} value={`${kpis.cost.fuel_total.toFixed(0)} EUR`} sub={`${kpis.cost.fuel_per_km.toFixed(2)} EUR/km`} />
                      <KPICard icon={Wrench} label={t('teams.detail.kpi.maintenanceCost')} value={`${kpis.cost.maintenance_total.toFixed(0)} EUR`} sub={t('teams.detail.kpi.perVehicle', { value: kpis.cost.maintenance_per_vehicle.toFixed(0) })} />
                      <KPICard icon={TrendingUp} label={t('teams.detail.kpi.km')} value={`${kpis.productivity.total_km.toFixed(0)} km`} />
                      <KPICard icon={Users} label={t('teams.detail.kpi.missions')} value={`${kpis.productivity.missions_completed}/${kpis.productivity.missions_total}`} sub={t('teams.detail.kpi.availability', { rate: kpis.productivity.availability_rate })} />
                      <KPICard icon={DollarSign} label={t('teams.detail.kpi.tireCost')} value={`${kpis.cost.tire_cost.toFixed(0)} EUR`} />
                      <KPICard icon={DollarSign} label={t('teams.detail.kpi.totalCost')} value={`${kpis.cost.total.toFixed(0)} EUR`} highlight />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">{t('teams.detail.kpi.empty')}</p>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="space-y-2">
                  {history.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">{t('teams.detail.history.empty')}</p>
                  ) : (
                    history.map((entry) => (
                      <div key={entry.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-2 h-2 rounded-full ${entry.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {entry.member_type} #{entry.member_id}
                            <span className="text-xs text-gray-500 ml-2">({entry.role})</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('teams.detail.history.joined', { date: fmt.date(entry.joined_at) })}
                            {entry.left_at && t('teams.detail.history.left', { date: fmt.date(entry.left_at) })}
                          </p>
                          {entry.leave_reason && (
                            <p className="text-xs text-gray-400 mt-0.5">{entry.leave_reason}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Member Picker Modal */}
      {showMemberPicker && (
        <MemberPickerModal
          memberType={showMemberPicker}
          onSelect={(memberId) => handleAddMember(showMemberPicker, memberId)}
          onClose={() => setShowMemberPicker(null)}
        />
      )}

      {/* Edit Form Modal */}
      {showEditForm && team && (
        <TeamFormModal
          team={team}
          onClose={() => setShowEditForm(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 capitalize">{value || '-'}</p>
    </div>
  );
}

function MemberSection({ title, icon: Icon, members, expanded, onToggle, onAdd, onRemove, renderItem, renderSub, removeLabel, addLabel }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{title}</span>
          <span className="text-xs text-gray-400">({members.length})</span>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="p-2 space-y-1">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
              <div>
                <p className="text-sm text-gray-900">{renderItem(m)}</p>
                {renderSub(m) && <p className="text-xs text-gray-500">{renderSub(m)}</p>}
              </div>
              <button
                onClick={() => onRemove(m.id, m.member_type, m.member_id)}
                className="p-1 text-gray-400 hover:text-red-500"
                title={removeLabel}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            onClick={onAdd}
            className="w-full flex items-center justify-center gap-1 py-2 text-xs text-indigo-600 hover:bg-indigo-50 rounded"
          >
            <Plus className="h-3.5 w-3.5" />
            {addLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, highlight }) {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${highlight ? 'text-indigo-600' : 'text-gray-400'}`} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className={`text-lg font-bold ${highlight ? 'text-indigo-700' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}
